import React, { useState, useEffect } from 'react';
import AzureCustomIntegrationField from './AzureCustomIntegrationField';
import JiraFieldSelectorComponent from './JiraFieldSelectorComponent';
import JiraProjectSelectorComponent from './JiraProjectSelectorComponent';
import HelpIcon from './HelpIcon';
import ScheduleSettings, { ScheduleData } from '../../settings/ScheduleSettings';
import { IntegrationField, IntegrationWrapper, INTEGRATION_TYPE_IDS } from '../types';
import { getCustomerIntegrationSecureConfigValues, deleteCustomerIntegration } from '../../../api';
import './spinner.css';



interface GenericIntegrationConfigProps {
  integration: IntegrationWrapper;
  configValues: Record<string, any>;
  configErrors: Record<string, string>;
  validating: boolean;
  validationSuccess: boolean;
  saving: boolean;
  onConfigChange: (name: string, value: string) => void;
  //onScheduleChange: (field: string, value: any) => void;
  onValidateConfig: () => void;
  onSaveConfig: () => void;
  customerIntegrations?: Array<{
    id: number;
    configValues: Record<string, any>;
    status: string | null;
    lastRunTime: string | null;
    nextScheduledRun: string | null;
    //monthsOfData: number | null;
    isEnabled: boolean;
  }>;
  onAddIntegration?: () => void;
  activeIntegrationIndex?: number;
  setActiveIntegrationIndex?: (index: number) => void;
  // This prop is no longer needed as integration removal is fully handled within this component
  // Kept for backward compatibility
  onIntegrationRemoved?: (index: number) => void;
}

const GenericIntegrationConfig: React.FC<GenericIntegrationConfigProps> = ({
  integration,
  configValues,
  configErrors,
  validating,
  validationSuccess,
  saving,
  onConfigChange,
  onValidateConfig,
  onSaveConfig,
  customerIntegrations = [],
  onAddIntegration,
  activeIntegrationIndex = 0,
  setActiveIntegrationIndex,
  onIntegrationRemoved,
}) => {
  // Local state for success and error messages
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isRemoving, setIsRemoving] = useState<boolean>(false);
  // Create a loading state
  const [isLoading, setIsLoading] = useState(validating);
  const allowMultiple = integration?.integration?.AllowMultiple || false;



  // Password visibility state (moved from renderField function to component level)
  const [passwordVisibility, setPasswordVisibility] = useState<Record<string, boolean>>({});

  // Set up local state for multiple integrations if needed
  const [localIntegrations, setLocalIntegrations] = useState<Array<{
    id: number | null;
    configValues: Record<string, any>;
    workItemType?: string;
  }>>([]);

  // Check if any fields should be stored in Key Vault
  const hasSecureFields = integration?.configFields?.some(field => field.storeInKeyVault === true) || false;

  // Cache for secure config values to avoid repeated API calls
  const [secureConfigCache, setSecureConfigCache] = useState<Record<string, Record<string, string>>>({});

  // Fetch secure config values when the component loads or when the active integration changes
  useEffect(() => {
    const fetchSecureConfigValues = async () => {
      if (hasSecureFields &&
        integration?.integration?.Id &&
        customerIntegrations &&
        customerIntegrations.length > 0 &&
        activeIntegrationIndex < customerIntegrations.length &&
        customerIntegrations[activeIntegrationIndex]?.id) {

        const integrationId = integration.integration.Id;
        const customerIntegrationId = customerIntegrations[activeIntegrationIndex].id;
        const cacheKey = `${integrationId}-${customerIntegrationId}`;

        // Check if we already have these secure values in cache
        if (secureConfigCache[cacheKey]) {
          // Use cached values
          Object.entries(secureConfigCache[cacheKey]).forEach(([key, value]) => {
            onConfigChange(key, value);
          });
          return;
        }

        try {
          setIsLoading(true);
          const response = await getCustomerIntegrationSecureConfigValues(
            integrationId,
            customerIntegrationId
          );

          if (response && response.secureConfigValues) {
            // Store in cache
            setSecureConfigCache(prev => ({
              ...prev,
              [cacheKey]: response.secureConfigValues
            }));

            // Update the config values in the parent component
            Object.entries(response.secureConfigValues).forEach(([key, value]) => {
              onConfigChange(key, value as string);
            });
          }
        } catch (error) {
          console.error('Error fetching secure config values:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchSecureConfigValues();
  }, [integration?.integration?.Id, activeIntegrationIndex, customerIntegrations, hasSecureFields]);



  // Initialize local integrations based on props
  useEffect(() => {
    if (allowMultiple && customerIntegrations.length > 0) {
      setLocalIntegrations(customerIntegrations);
    } else if (allowMultiple && customerIntegrations.length === 0) {
      // Initialize with one empty integration
      setLocalIntegrations([{
        id: null,
        configValues: { ...configValues }
      }]);
    }
  }, [allowMultiple, customerIntegrations, configValues]);

  // Handle adding a new integration
  const handleAddIntegration = () => {
    if (onAddIntegration) {
      onAddIntegration();
    } else {
      setLocalIntegrations([...localIntegrations, {
        id: null,
        configValues: { ...configValues }
      }]);
      if (setActiveIntegrationIndex) {
        setActiveIntegrationIndex(localIntegrations.length);
      }
    }
  };

  // Handle removing an integration - complete implementation with API calls
  const handleRemoveIntegration = async (index: number) => {
    // Don't allow removing the last integration
    if (customerIntegrations.length <= 1) return;

    // Clear any previous messages
    setSuccessMessage('');
    setErrorMessage('');

    // Get the customer integration ID to delete
    const customerIntegrationId = customerIntegrations[index]?.id;

    if (!customerIntegrationId || customerIntegrationId === 0) {
      // If there's no ID or ID is 0, it's a new integration that hasn't been saved yet
      // Just remove it from the local state
      const newIntegrations = [...customerIntegrations];
      newIntegrations.splice(index, 1);

      // Update local state
      setLocalIntegrations(newIntegrations);

      // Directly modify the customerIntegrations array to ensure parent state is updated
      // This works because arrays are passed by reference in JavaScript
      customerIntegrations.splice(index, 1);

      // Adjust the active index if needed
      if (activeIntegrationIndex >= index && setActiveIntegrationIndex) {
        const newIndex = Math.max(0, activeIntegrationIndex - 1);
        setActiveIntegrationIndex(newIndex);
      }
    } else {
      // If there's an ID, delete it from the backend
      try {
        setIsRemoving(true);
        await deleteCustomerIntegration(customerIntegrationId);

        // Remove the integration from the local array
        const newIntegrations = [...customerIntegrations];
        newIntegrations.splice(index, 1);

        // Update local state
        setLocalIntegrations(newIntegrations);

        // Directly modify the customerIntegrations array to ensure parent state is updated
        // This works because arrays are passed by reference in JavaScript
        customerIntegrations.splice(index, 1);

        // Adjust the active index if needed
        if (activeIntegrationIndex >= index && setActiveIntegrationIndex) {
          const newIndex = Math.max(0, activeIntegrationIndex - 1);
          setActiveIntegrationIndex(newIndex);
        }

        // Show success message
        setSuccessMessage('Integration removed successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        console.error('Error removing integration:', error);
        setErrorMessage('Failed to remove integration');
        setTimeout(() => setErrorMessage(''), 5000);
      } finally {
        setIsRemoving(false);
      }
    }
  };



  // Enhanced validate and save handlers
  const handleValidate = () => {

    onValidateConfig();
  };

  const handleSave = () => {

    onSaveConfig();
  };

  // Render field based on type
  const renderField = (field: IntegrationField) => {
    const value = configValues && configValues[field.name] ? configValues[field.name] : '';
    const error = configErrors && configErrors[field.name] ? configErrors[field.name] : '';

    const commonLabelStyles = {
      display: 'block',
      marginBottom: 8,
      fontWeight: 500
    };

    const commonInputStyles = {
      width: '100%',
      padding: '8px 12px',
      borderRadius: 4,
      border: `1px solid ${error ? '#f44336' : '#ccc'}`,
      fontSize: 14,
      boxSizing: 'border-box' as const
    };

    const errorMessageStyles = {
      color: '#f44336',
      fontSize: 12,
      marginTop: 4
    };

    switch (field.type) {
      case 'custom':
        // Handle custom field types based on integration type
        if (integration.integration.Id === INTEGRATION_TYPE_IDS.AZURE_DEVOPS) {
          // Pass all Azure DevOps custom fields to AzureCustomIntegrationField
          return (
            <AzureCustomIntegrationField
              key={field.name}
              field={field}
              value={value}
              error={error}
              integrationTypeId={integration.integration.Id}
              customerIntegrationId={integration.customerIntegrations && integration.customerIntegrations.length > 0 ? integration.customerIntegrations[0].id : null}
              configValues={configValues}
              onChange={onConfigChange}
            />
          );
        }
        if(integration.integration.Id === INTEGRATION_TYPE_IDS.JIRA) {
          // Handle different Jira custom field types
          if (field.name === 'projectKey') {
            return (
              <JiraProjectSelectorComponent
                key={field.name}
                field={field}
                value={value}
                error={error}
                integrationTypeId={integration.integration.Id}
                customerIntegrationId={integration.customerIntegrations && integration.customerIntegrations.length > 0 ? integration.customerIntegrations[0].id : null}
                configValues={configValues}
                onChange={onConfigChange}
              />
            );
          } else {
            // For sprintFieldId and storyPointsFieldId, use JiraFieldSelectorComponent
            return (
              <JiraFieldSelectorComponent
                key={field.name}
                field={field}
                value={value}
                error={error}
                integrationTypeId={integration.integration.Id}
                customerIntegrationId={integration.customerIntegrations && integration.customerIntegrations.length > 0 ? integration.customerIntegrations[0].id : null}
                configValues={configValues}
                onChange={onConfigChange}
              />
            );
          }
        }
        // Add more integration-specific custom field handlers as needed
        return null;

      case 'text':
        return (
          <div key={field.name} style={{ marginBottom: 16 }}>
            <label htmlFor={field.name} style={{ ...commonLabelStyles, display: 'flex', alignItems: 'center' }}>
              <span>
                {field.label}{field.required && <span style={{ color: 'red' }}> *</span>}
              </span>
              {field.helpText && <HelpIcon helpText={field.helpText} fieldLabel={field.label} />}
            </label>
            <input
              type="text"
              id={field.name}
              name={field.name}
              value={value}
              onChange={(e) => onConfigChange(field.name, e.target.value)}
              required={field.required}
              style={commonInputStyles}
            />
            {error && <div style={errorMessageStyles}>{error}</div>}
          </div>
        );

      case 'password':
        const showPassword = passwordVisibility[field.name] || false;

        return (
          <div key={field.name} style={{ marginBottom: 16 }}>
            <label htmlFor={field.name} style={{ ...commonLabelStyles, display: 'flex', alignItems: 'center' }}>
              <span>
                {field.label}{field.required && <span style={{ color: 'red' }}> *</span>}
              </span>
              {field.helpText && <HelpIcon helpText={field.helpText} fieldLabel={field.label} />}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id={field.name}
                name={field.name}
                value={value}
                onChange={(e) => onConfigChange(field.name, e.target.value)}
                required={field.required}
                style={commonInputStyles}
              />
              <button
                type="button"
                onClick={() => setPasswordVisibility(prev => ({ ...prev, [field.name]: !showPassword }))}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {error && <div style={errorMessageStyles}>{error}</div>}
          </div>
        );

      case 'number':
        return (
          <div key={field.name} style={{ marginBottom: 16 }}>
            <label htmlFor={field.name} style={{ ...commonLabelStyles, display: 'flex', alignItems: 'center' }}>
              <span>
                {field.label}{field.required && <span style={{ color: 'red' }}> *</span>}
              </span>
              {field.helpText && <HelpIcon helpText={field.helpText} fieldLabel={field.label} />}
            </label>
            <input
              type="number"
              id={field.name}
              name={field.name}
              value={value}
              onChange={(e) => onConfigChange(field.name, e.target.value)}
              required={field.required}
              style={commonInputStyles}
            />
            {error && <div style={errorMessageStyles}>{error}</div>}
          </div>
        );

      case 'date':
        return (
          <div key={field.name} style={{ marginBottom: 16 }}>
            <label htmlFor={field.name} style={{ ...commonLabelStyles, display: 'flex', alignItems: 'center' }}>
              <span>
                {field.label}{field.required && <span style={{ color: 'red' }}> *</span>}
              </span>
              {field.helpText && <HelpIcon helpText={field.helpText} fieldLabel={field.label} />}
            </label>
            <input
              type="date"
              id={field.name}
              name={field.name}
              value={value}
              onChange={(e) => onConfigChange(field.name, e.target.value)}
              required={field.required}
              style={commonInputStyles}
            />
            {error && <div style={errorMessageStyles}>{error}</div>}
          </div>
        );

      case 'boolean':
        return (
          <div key={field.name} style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                id={field.name}
                name={field.name}
                checked={value === true || value === 'true'}
                onChange={(e) => onConfigChange(field.name, e.target.checked.toString())}
                style={{ marginRight: 8 }}
              />
              <span>
                {field.label}{field.required && <span style={{ color: 'red' }}> *</span>}
              </span>
              {field.helpText && <HelpIcon helpText={field.helpText} fieldLabel={field.label} />}
            </label>
            {error && <div style={errorMessageStyles}>{error}</div>}
          </div>
        );

      case 'hidden':
        return (
          <div key={field.name} style={{ marginBottom: 16 }}>
            <input
              type="hidden"
              id={field.name}
              name={field.name}
              value={value}
              onChange={(e) => onConfigChange(field.name, e.target.value)}
            />
          </div>
        );
      default:
        return (
          <div key={field.name} style={{ marginBottom: 16 }}>
            <p>Unknown field type: {field.type}</p>
          </div>
        );
    }
  };

  // Render a single configuration component
  const renderConfigComponent = (index: number = 0) => (
    <div style={{ position: 'relative' }}>
      {/* Loading overlay for the entire form */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10
        }}>
          <div className="spinner"></div>
        </div>
      )}

      <h3 style={{ marginBottom: 24 }}>
        {integration?.integration?.Name} Configuration
      </h3>

      <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
        Enter the required information to connect to your {integration?.integration?.Name} account.
      </p>

      {/* Dynamically render fields based on config */}
      <div style={{ marginBottom: 24 }}>
        {integration?.configFields?.map((field) => renderField(field))}
      </div>


      {/* Validate and Save buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <button
          onClick={handleValidate}
          disabled={validating}
          style={{
            padding: '8px 16px',
            backgroundColor: validating ? '#bdbdbd' : '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: validating ? 'not-allowed' : 'pointer'
          }}
        >
          {validating ? 'Validating...' : 'Validate Connection'}
        </button>

        <button
          onClick={handleSave}
          disabled={saving || !validationSuccess}
          style={{
            padding: '8px 16px',
            backgroundColor: (saving || !validationSuccess) ? '#bdbdbd' : '#4caf50',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: (saving || !validationSuccess) ? 'not-allowed' : 'pointer',
            opacity: (saving || !validationSuccess) ? 0.7 : 1
          }}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
        {validationSuccess && (
          <span style={{ color: 'green' }}>
            ✓ Connection validated
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {/* Success message */}
      {successMessage && (
        <div style={{
          padding: 16,
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          borderRadius: 4,
          marginBottom: 24
        }}>
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {errorMessage && (
        <div style={{
          padding: 16,
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: 4,
          marginBottom: 24
        }}>
          {errorMessage}
        </div>
      )}

      {allowMultiple ? (
        <div>
          {/* Tabs for multiple integrations */}
          {localIntegrations.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {localIntegrations.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIntegrationIndex && setActiveIntegrationIndex(index)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: activeIntegrationIndex === index ? '#1976d2' : '#f0f0f0',
                    color: activeIntegrationIndex === index ? 'white' : 'black',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Configuration {index + 1}
                </button>
              ))}
              <button
                onClick={handleAddIntegration}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                + Add Configuration
              </button>
            </div>
          )}

          {/* Active configuration component */}
          {localIntegrations.map((_, index) => (
            <div key={index} style={{ display: activeIntegrationIndex === index ? 'block' : 'none' }}>
              {renderConfigComponent(index)}
              {localIntegrations.length > 1 && (
                <div style={{ marginTop: '16px', textAlign: 'right' }}>
                  <button
                    onClick={() => handleRemoveIntegration(index)}
                    disabled={isRemoving}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isRemoving ? 'not-allowed' : 'pointer',
                      opacity: isRemoving ? 0.7 : 1
                    }}
                  >
                    {isRemoving ? 'Removing...' : 'Remove Configuration'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        renderConfigComponent()
      )}
    </div>
  );
};

export default GenericIntegrationConfig;
